import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import ImageWithPaintPickerArticle from '@modules/page-builder/component/common/image-with-paint-picker/ImageWithPaintPickerArticle';
import { ModuleColumnEnum } from 'generated/graphql';
import type {
    ModuleDataColumn,
    PageBuilderModuleMetaProps,
} from '@modules/page-builder/types';
import { SINGLE_COLUMN_WIDTH } from '@modules/page-builder/config/column-constants';
import DetailSingleImageShell from '../detail-single-image-shell/DetailSingleImageShell';
import SingleParagraphModule from '../single-paragraph-module/SingleParagraphModule';

import { getImagesSizes } from '~/utils/image-sizes-utils';
import ProductInfoArticle from '@modules/page-builder/component/common/product-info/ProductInfoArticle';

interface ImageParagraphModuleProps extends PageBuilderModuleMetaProps {
    columns: ModuleDataColumn[];
}

/**
 * A rendered Page Builder module for an image and paragraph.
 *
 * The editor version is at
 * {@link packages/web/app/modules/page-builder/component/edit/image-paragraph-module/ImageParagraphModule.tsx}
 */
const ImageParagraphModule = ({ columns, meta }: ImageParagraphModuleProps) => {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);

    return (
        <div className="mx-auto grid w-full grid-cols-1 gap-x-6 gap-y-7 *:min-h-0 md:max-w-225 md:grid-cols-2">
            {columns.map((column) => {
                if (column.type === ModuleColumnEnum.ImageColumn) {
                    if (!column.imageUrl && isMobile) {
                        return null;
                    }
                    // Create constrained dimensions for paragraph module
                    const constrainedHeight = Math.round(
                        (SINGLE_COLUMN_WIDTH * column.dimension.height) /
                            column.dimension.width,
                    );
                    const paragraphDimension = {
                        ...column.dimension,
                        width: SINGLE_COLUMN_WIDTH,
                        height: constrainedHeight,
                        tabletWidth: SINGLE_COLUMN_WIDTH,
                        tabletHeight: constrainedHeight,
                        mobileWidth: 0,
                        mobileHeight: constrainedHeight,
                    };

                    const imageWithCaption = column.imageUrl ? (
                        <>
                            <div
                                key={column.id}
                                className={`relative block max-w-full md:flex md:w-[${SINGLE_COLUMN_WIDTH}px] md:h-[${paragraphDimension.height}px] md:shrink-0`}
                            >
                                <DetailSingleImageShell
                                    dimension={paragraphDimension}
                                    libraryImage={column?.libraryImage}
                                    tags={column.tags}
                                    uniqueApprovedTags={
                                        column.uniqueApprovedTags
                                    }
                                    imageURL={column.imageUrl}
                                    columnId={column.id}
                                    taggingEnabled={!column.productId}
                                    displayReseneTool={
                                        !column.productId && false
                                    }
                                    isProduct={!!column.productId}
                                >
                                    <ImageWithPaintPickerArticle
                                        src={column.imageUrl}
                                        layout={'fill'}
                                        objectFit={'cover'}
                                        width={paragraphDimension.width}
                                        height={paragraphDimension.height}
                                        sizes={getImagesSizes(
                                            SINGLE_COLUMN_WIDTH,
                                        )}
                                        alt={
                                            column.caption ||
                                            column?.libraryImage?.Title ||
                                            ''
                                        }
                                        className="overflow-hidden md:rounded-2"
                                        variables={{
                                            mixBlendMode: column.productId
                                                ? 'multiply'
                                                : 'normal',
                                        }}
                                    />
                                </DetailSingleImageShell>
                            </div>
                            {column.caption && (
                                <div className="mt-2 px-5 text-sm text-gray-600 md:px-0">
                                    {column.caption}
                                </div>
                            )}
                        </>
                    ) : (
                        <div key={column.id}></div>
                    );

                    return column.productId ? (
                        <div
                            key={column.id}
                            className="scale-down-variant-caption flex min-h-0 flex-col"
                        >
                            <ProductInfoArticle
                                productId={column.productId}
                                dimension={paragraphDimension}
                                variant={'02'}
                                image={imageWithCaption}
                                clickable={true}
                            />
                        </div>
                    ) : (
                        <div key={column.id} className="flex min-h-0 flex-col">
                            {imageWithCaption}
                        </div>
                    );
                } else if (column.type === ModuleColumnEnum.TextColumn) {
                    if (!column.content && isMobile) {
                        return null;
                    }
                    return (
                        <SingleParagraphModule
                            key={column.id}
                            column={column}
                            meta={meta}
                        />
                    );
                }
                return null;
            })}
        </div>
    );
};

export default ImageParagraphModule;
